/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Card, Text, VStack, Box, Badge, Tooltip, SimpleGrid, GridItem, Button, TableContainer } from '@chakra-ui/react';
import { Product } from './interfaces';
import { SmartphoneFrame, SIMULATOR_LEVELS } from './atoms/SmartphoneFrame';
import { getProductsForLevel } from './utils/simulatorHelpers';

interface IPlanERP {
  id: number;
  id_plan: number;
  name_plan: string;
  value_plan: number;
  value?: number;
  count_clients_active: number;
  count_clients?: number;
  plan_slug_integration?: string;
  erp: string;
  products: Array<{
    id?: number;
    plan: number;
    product: number;
    active?: boolean;
    map_integration_plan?: number;
  }>;
}

interface Step4SimulatorsProps {
  products: Product[];
  filteredAndSortedProductsERP: IPlanERP[];
  sortOrder?: 'asc' | 'desc' | null;
  setSortOrder?: (order: 'asc' | 'desc' | null) => void;
}

export const Step4Simulators: React.FC<Step4SimulatorsProps> = ({
  products,
  filteredAndSortedProductsERP,
  sortOrder,
  setSortOrder,
}) => {
  if (!filteredAndSortedProductsERP || !Array.isArray(filteredAndSortedProductsERP) || filteredAndSortedProductsERP.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
        Nenhum plano encontrado com os critérios de busca
      </Text>
    );
  }

  const sortedProductsERP = [...filteredAndSortedProductsERP].sort((a, b) => {
    if (!sortOrder) return 0;

    const valueA = a.value_plan ?? a.value ?? 0;
    const valueB = b.value_plan ?? b.value ?? 0;

    if (sortOrder === 'asc') {
      return valueA - valueB;
    }
    return valueB - valueA;
  });

  const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null);

  // SVA = default_product.id === 4 (igual aos outros steps)
  const produtosPartiuProducts = products.filter((product) => product.default_product?.id !== 4);
  const svaProducts = products.filter((product) => product.default_product?.id === 4);
  const sortedProducts = [...produtosPartiuProducts, ...svaProducts];

  const buttonColumnWidth = 210;
  const totalWidth = 60 + 250 + 110 + 80 + buttonColumnWidth;

  return (
    <VStack spacing={2} width="100%">
      <Card width="100%" boxShadow="sm">
        <TableContainer maxH="70vh" overflowY="auto" overflowX="auto" position="relative" sx={{ '&::-webkit-scrollbar': { width: '8px', height: '8px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' } }}>
          <Box
            display="grid"
            gridTemplateColumns={`60px 250px 110px 80px ${buttonColumnWidth}px`}
            bg="gray.50"
            borderBottom="2px solid"
            borderColor="gray.300"
            px={0}
            position="sticky"
            top={0}
            zIndex={20}
            minWidth={`${totalWidth}px`}
            width={`${totalWidth}px`}
            boxShadow="0 2px 4px rgba(0,0,0,0.08)"
          >
            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              borderRight="1px solid"
              borderColor="gray.200"
              textAlign="center"
              width="60px"
              bg="gray.50"
              py={2}
              position="sticky"
              left={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={12}
            >
              ID
            </Box>

            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="left"
              width="250px"
              minW="250px"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              px={3}
              borderRight="1px solid"
              borderColor="gray.200"
              bg="gray.50"
              py={2}
              position="sticky"
              left="60px"
              zIndex={12}
            >
              Nome do Plano
            </Box>

            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="center"
              width="110px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="gray.50"
              py={1}
              gap={3}
              borderRight="1px solid"
              borderColor="gray.200"
              position="sticky"
              left="310px"
              zIndex={12}
            >
              <Text fontSize="12px" fontWeight="bold">
                VALOR
              </Text>
              {setSortOrder && (
                <Box
                  as="button"
                  onClick={() => {
                    if (sortOrder === null) {
                      setSortOrder('asc');
                    } else if (sortOrder === 'asc') {
                      setSortOrder('desc');
                    } else {
                      setSortOrder(null);
                    }
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="20px"
                  h="20px"
                  borderRadius="4px"
                  bg={sortOrder ? 'blue.50' : 'gray.50'}
                  border="1px solid"
                  borderColor={sortOrder ? 'blue.200' : 'gray.200'}
                  color={sortOrder ? 'blue.600' : 'gray.400'}
                  cursor="pointer"
                  _hover={{
                    bg: sortOrder ? 'blue.100' : 'gray.100',
                    borderColor: sortOrder ? 'blue.300' : 'gray.300',
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s ease"
                  title={
                    sortOrder === null
                      ? 'Ordenar por valor'
                      : sortOrder === 'asc'
                        ? 'Menor para maior'
                        : 'Maior para menor'
                  }
                >
                  <Text fontSize="10px">
                    {sortOrder === null ? '↕' : sortOrder === 'asc' ? '↑' : '↓'}
                  </Text>
                </Box>
              )}
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              borderRight="1px solid"
              borderColor="gray.200"
              py={2}
              width="80px"
              minW="80px"
              maxW="80px"
            >
              <VStack spacing={0}>
                <Text fontSize="xs">USUÁRIOS</Text>
                <Text fontSize="xs">ATIVOS</Text>
              </VStack>
            </Box>

            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="center"
              bg="gray.50"
              py={2}
              px={2}
            >
              Simuladores
            </Box>
          </Box>

          <Box>
            {sortedProductsERP.map((productERP) => {
              const simulatorProducts = SIMULATOR_LEVELS.map(({ key, rank }) => ({
                key,
                products: getProductsForLevel(sortedProducts, productERP, rank),
              }));
              const isExpanded = expandedPlanId === productERP.id;

              return (
                <Box key={productERP.id} borderBottom="1px solid" borderColor="gray.200">
                  <Box
                    display="grid"
                    gridTemplateColumns={`60px 250px 110px 80px ${buttonColumnWidth}px`}
                    minWidth={`${totalWidth}px`}
                    width={`${totalWidth}px`}
                    minH="60px"
                    alignItems="center"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRight="1px solid"
                      borderColor="gray.200"
                      py={1}
                      height="100%"
                      position="sticky"
                      left={0}
                      zIndex={12}
                      bg="white"
                      width="60px"
                      minW="60px"
                      maxW="60px"
                    >
                      <Tooltip label="Identificador único do plano ERP no sistema" placement="top">
                        <Text fontSize="sm" fontWeight="bold" color="gray.600" maxW="60px">
                          {productERP.id_plan ?? productERP.id ?? '-'}
                        </Text>
                      </Tooltip>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      borderRight="1px solid"
                      borderColor="gray.200"
                      py={1}
                      position="sticky"
                      left="60px"
                      zIndex={12}
                      bg="white"
                      px={3}
                      width="250px"
                      minW="250px"
                      maxW="250px"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color="gray.800"
                        lineHeight="1.2"
                        wordBreak="break-word"
                        textAlign="left"
                        whiteSpace="normal"
                        maxW="100%"
                      >
                        {productERP.name_plan || productERP.plan_slug_integration || `Plano #${productERP.id_plan ?? productERP.id}`}
                      </Text>
                    </Box>

                    <Box
                      textAlign="center"
                      py={1}
                      px={2}
                      borderRight="1px solid"
                      borderColor="gray.200"
                      width="110px"
                      position="sticky"
                      left="310px"
                      zIndex={12}
                      bg="white"
                      minW="110px"
                      maxW="110px"
                    >
                      <Tooltip label="Valor mensal do plano ERP" placement="top">
                        <Box
                          bg="blue.50"
                          border="1px solid"
                          borderColor="blue.200"
                          color="blue.700"
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="xs"
                          fontWeight="semibold"
                          textAlign="center"
                          _hover={{
                            bg: 'blue.100',
                            borderColor: 'blue.300',
                          }}
                          transition="all 0.2s"
                        >
                          {Number(productERP.value_plan ?? productERP.value ?? 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </Box>
                      </Tooltip>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRight="1px solid"
                      borderColor="gray.200"
                      py={2}
                      width="80px"
                      minW="80px"
                      maxW="80px"
                      bg="white"
                    >
                      <Tooltip label="Total de usuários ativos atualmente neste plano ERP" placement="top">
                        <Badge colorScheme="green" fontSize="xs" variant="solid" textAlign="center">
                          {(productERP.count_clients_active ?? productERP.count_clients ?? 0).toLocaleString('pt-BR')}
                        </Badge>
                      </Tooltip>
                    </Box>

                    <Box display="flex" alignItems="center" justifyContent="center" py={2} px={3}>
                      <Button
                        size="sm"
                        colorScheme={isExpanded ? 'green' : 'blue'}
                        variant={isExpanded ? 'solid' : 'outline'}
                        onClick={() => setExpandedPlanId(isExpanded ? null : productERP.id)}
                        width="100%"
                        maxW="180px"
                      >
                        {isExpanded ? 'Esconder simuladores' : 'Ver simuladores'}
                      </Button>
                    </Box>
                  </Box>

                  {isExpanded && (
                    <Box bg="gray.50" px={4} py={4} borderTop="1px solid" borderColor="gray.100">
                      <Text fontSize="sm" fontWeight="bold" mb={4} color="gray.700">
                        Simuladores - {productERP.name_plan || productERP.plan_slug_integration || `Plano #${productERP.id_plan ?? productERP.id}`}
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 5 }} spacing={4}>
                        {simulatorProducts.map(({ key, products }) => (
                          <GridItem key={key}>
                            <SmartphoneFrame plan={key} products={products} variant="compact" />
                          </GridItem>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </TableContainer>
      </Card>
    </VStack>
  );
};

